'use client';

import React, { forwardRef, useEffect, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { PageType, useGTM } from '@/features/Analytics';
import { BookingTrigger } from '@/features/Booking';
import { TriggerPhonePreview } from '@/features/Phones/ui/TriggerPhonePreview';
import { TriggerPhones } from '@/features/Phones/ui/TriggerPhones';
import { classNames } from '@/shared/lib/classnames/classnames';
import { useDoctor } from '../../model/store';
import type { IBookingDoctorData } from '@/entities/booking/model/types';
import type { IDoctorDetails } from '@/entities/doctor/model/types';
import type { IMedcenter } from '@/entities/medcenter/model/types';
interface Props {
  medcenters: IMedcenter[];
  classes?: string;
  doctor: IDoctorDetails;
  isBottomBar?: boolean;
  bookingData: IBookingDoctorData[];
  phoneNumber?: string;
}
const BookingButtons = forwardRef<HTMLDivElement, Props>((props, ref) => {
  const {
    medcenters,
    doctor,
    classes,
    bookingData,
    isBottomBar = false,
    phoneNumber
  } = props;
  const t = useTranslations();
  const {
    trackEvent
  } = useGTM(PageType.DOCTOR_PROFILE);
  const {
    medcenterId,
    setDoctorId,
    setDoctorName,
    setMedcenterName
  } = useDoctor();
  const hasOnline = useMemo(() => {
    if (medcenterId) {
      const booking = bookingData.find(item => item.medcenterId === medcenterId);
      const medcenter = medcenters.find(item => item.id === medcenterId);
      if (medcenter && !medcenter.hasOnlineBooking) {
        return false;
      }
      if (booking) {
        const {
          timeslotsIsEmpty,
          basedOn,
          schedule
        } = booking.onlineBooking;
        if ((timeslotsIsEmpty || schedule?.length === 0) && basedOn !== 'preferred_time') return false;
        return true;
      }
    }
    return false;
  }, [medcenterId, medcenters]);
  const currentMedcenter = useMemo(() => {
    if (medcenterId) {
      return medcenters?.find(medcenter => medcenter.id === medcenterId) || null;
    } else {
      return medcenters?.length ? medcenters[0] : null;
    }
  }, [medcenterId, medcenters]);
  const hasPhonesForCurrentMedcenter = useMemo(() => {
    const doctorMedcenter = doctor.medcenters?.find(mc => mc.id === (currentMedcenter?.id || 0));
    return doctorMedcenter?.hasPhones || false;
  }, [currentMedcenter, doctor.medcenters]);
  const handleShowPhones = () => {
    trackEvent('click_show_number', {
      medcenterName: currentMedcenter ? currentMedcenter.name : 'no_medcenter'
    });
  };
  const handleShowPhonePreview = () => {
    trackEvent('view_popup', {
      medcenterName: currentMedcenter ? currentMedcenter.name : 'no_medcenter'
    });
  };
  useEffect(() => {
    setDoctorId(doctor.id);
    setDoctorName(doctor.fullName);
    setMedcenterName(currentMedcenter?.name || '');
  }, [doctor]);
  return <>
      <div ref={ref} data-booking-buttons={!isBottomBar ? 'true' : undefined} className={classNames('flex', {
      'flex-col': !isBottomBar,
      'w-full items-center gap-1': isBottomBar
    }, [classes])}>
        {hasOnline && <BookingTrigger modalId={doctor.id} btnVariants={{
        size: isBottomBar ? 'sm' : 'default'
      }} btnClass={classNames('', {
        'w-full': isBottomBar
      })} />}
        {doctor.isPartner && hasOnline && hasPhonesForCurrentMedcenter && <TriggerPhonePreview doctorImage={doctor.avatar} phoneNumber={phoneNumber || ''} doctorId={doctor.id} hasOnline={hasOnline} medcenterId={currentMedcenter?.id} onShowPhonesGtmEvent={handleShowPhonePreview} btnVariants={{
        size: isBottomBar ? 'icon' : 'default'
      }} isFull={isBottomBar && !hasOnline} isIcon={isBottomBar && hasOnline} className={isBottomBar && hasOnline ? 'size-10 p-0' : ''} />}
        {hasPhonesForCurrentMedcenter && !hasOnline && <TriggerPhones doctorId={doctor.id} isIcon={isBottomBar && hasOnline} hasOnline={hasOnline} medcenterId={currentMedcenter?.id} onShowPhonesGtmEvent={handleShowPhones} btnText={isBottomBar && hasOnline ? '' : t('bookingPage.show_phone')} isFull={isBottomBar && !hasOnline} />}
      </div>
    </>;
});
BookingButtons.displayName = 'BookingButtons';
export { BookingButtons };